// Minimal predicate engine for wizard branching rules. Admins author
// rules as { fromStageId, when, goto } triples in wizard_config; this
// evaluator decides which stage to render next.
//
// We use a tiny subset of jsonlogic — enough for the rules an admin
// would reasonably author from a UI ("plan picked is in this list",
// "intent equals X", "field is empty") without pulling in the full
// jsonlogic-js dependency. The operators below cover the common
// cases; the format is forward-compatible with jsonlogic so we can
// swap in the full library later without changing rule storage.

import type { WizardConfig, WizardStage, DraftPayload } from './draftClient';

type JsonLogicValue = unknown;
type JsonLogicRule = JsonLogicValue;

/**
 * Get a dotted path from the payload. "contact.email" → payload.contact.email.
 * Returns undefined for missing keys (not an error).
 */
function getPath(payload: DraftPayload, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, payload);
}

/**
 * Evaluate a single jsonlogic-shaped rule against the payload.
 * Supported operators:
 *   { "var": "contact.email" }                — read a path
 *   { "==": [a, b] }                          — strict equality
 *   { "!=": [a, b] }                          — strict inequality
 *   { "in": [a, [b, c]] }                     — array membership
 *   { "and": [r1, r2, ...] } / { "or": ... }  — boolean composition
 *   { "!": r }                                — negation
 *   { "empty": r } / { "!empty": r }          — null/undefined/'' checks
 * Literals (strings, numbers, bools, arrays of literals) pass through.
 */
export function evaluate(rule: JsonLogicRule, payload: DraftPayload): unknown {
  if (rule === null || rule === undefined) return rule;
  if (typeof rule !== 'object') return rule;
  if (Array.isArray(rule)) return rule.map((r) => evaluate(r, payload));

  const entries = Object.entries(rule as Record<string, JsonLogicValue>);
  if (entries.length !== 1) return rule;
  const [op, raw] = entries[0]!;
  const args = Array.isArray(raw) ? raw.map((a) => evaluate(a, payload)) : evaluate(raw, payload);

  switch (op) {
    case 'var':
      return getPath(payload, String(args));
    case '==':
      return Array.isArray(args) && args[0] === args[1];
    case '!=':
      return Array.isArray(args) && args[0] !== args[1];
    case 'in':
      return Array.isArray(args) && Array.isArray(args[1]) && (args[1] as unknown[]).includes(args[0]);
    case 'and':
      return Array.isArray(args) && args.every(Boolean);
    case 'or':
      return Array.isArray(args) && args.some(Boolean);
    case '!':
      return !args;
    case 'empty':
      return args === null || args === undefined || args === '';
    case '!empty':
      return !(args === null || args === undefined || args === '');
    default:
      return undefined;
  }
}

/**
 * Compute the next stage. Walks branches in order; first truthy match
 * wins. Falls through to the next stage in the ordered stages array.
 * Returns null when the current stage is the last one and no branch
 * matched — caller treats that as "show ReviewStep / submit".
 */
export function nextStage(
  config: WizardConfig,
  currentStageId: string,
  payload: DraftPayload,
): WizardStage | null {
  const fromBranch = config.branches
    .filter((b) => b.fromStageId === currentStageId)
    .find((b) => Boolean(evaluate(b.when as JsonLogicRule, payload)));
  if (fromBranch) {
    return config.stages.find((s) => s.id === fromBranch.goto) ?? null;
  }
  const idx = config.stages.findIndex((s) => s.id === currentStageId);
  if (idx < 0 || idx >= config.stages.length - 1) return null;
  return config.stages[idx + 1] ?? null;
}

export function previousStage(
  config: WizardConfig,
  currentStageId: string,
): WizardStage | null {
  // Branches don't have a reverse; back-button uses the linear array.
  // Customers backing into a stage that the branching engine would
  // have skipped just see an empty form they can leave blank — not a
  // huge deal vs. modeling a navigation stack.
  const idx = config.stages.findIndex((s) => s.id === currentStageId);
  if (idx <= 0) return null;
  return config.stages[idx - 1] ?? null;
}

export function firstStage(config: WizardConfig): WizardStage | null {
  return config.stages[0] ?? null;
}
